import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api/client';
import { useAdminUser, useDeactivateAdminUser } from '@/lib/api/users';
import { ROLE_LABELS } from '@/lib/auth/roles';

export function UserEditPage({ userId }: { userId: string }) {
  const userQuery = useAdminUser(userId);
  const deactivate = useDeactivateAdminUser();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const onDeactivate = async () => {
    setError(null);
    try {
      await deactivate.mutateAsync(userId);
      void navigate({ to: '/users' });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not deactivate the user.');
    }
  };

  if (userQuery.isPending) {
    return <p className="text-sm">Loading…</p>;
  }

  if (userQuery.isError) {
    return (
      <p className="text-sm text-[var(--color-destructive)]">
        Failed to load user: {userQuery.error.message}
      </p>
    );
  }

  const user = userQuery.data;
  if (!user) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-[var(--color-muted-foreground)]">Admin not found.</p>
        <Button asChild variant="outline">
          <Link to="/users">Back to admins</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Edit admin</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">{user.email}</p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
          <CardDescription>Email is set at invite time and can't be changed here.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Email</Label>
            <p className="text-sm">{user.email}</p>
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <p className="text-sm">{ROLE_LABELS[user.role]}</p>
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <div>
              {user.isActive ? (
                <Badge variant="secondary">Active</Badge>
              ) : (
                <Badge variant="outline">Inactive</Badge>
              )}
            </div>
          </div>
          {error && <p className="text-sm text-[var(--color-destructive)]">{error}</p>}
          <div className="flex items-center gap-2">
            {user.isActive && (
              <Button
                variant="outline"
                disabled={deactivate.isPending}
                onClick={() => void onDeactivate()}
              >
                {deactivate.isPending ? 'Deactivating…' : 'Deactivate'}
              </Button>
            )}
            <Button asChild variant="outline" type="button">
              <Link to="/users">Back</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
